import React, { forwardRef, useCallback, useEffect, useState } from 'react';
import { Animated, Easing, StyleProp, ViewStyle } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { useTheme } from '@/contexts/theme-context';

const rgbaToHex = (rgba: string): string => {
  const rgbaRegex =
    /^rgba?\((\d{1,3}),\s*(\d{1,3}),\s*(\d{1,3}),?\s*([\d.]+)?\)$/;
  const result = rgba.match(rgbaRegex);

  if (!result) {
    throw new Error(`Invalid RGBA string format: ${rgba}`);
  }

  const r = parseInt(result[1], 10);
  const g = parseInt(result[2], 10);
  const b = parseInt(result[3], 10);
  const a = result[4] ? parseFloat(result[4]) : 1;

  if (
    r < 0 ||
    r > 255 ||
    g < 0 ||
    g > 255 ||
    b < 0 ||
    b > 255 ||
    a < 0 ||
    a > 1
  ) {
    throw new Error(
      `Invalid RGBA values: r=${r}, g=${g}, b=${b}, a=${a}. Argument rgba: ${rgba}`,
    );
  }

  const componentToHex = (component: number): string => {
    const hex = component.toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };

  const alphaHex = componentToHex(Math.round(a * 255));

  return `#${componentToHex(r)}${componentToHex(g)}${componentToHex(
    b,
  )}${alphaHex}`;
};

interface GradientProps {
  children?: React.ReactNode;
  color0: string;
  color1: string;
  points: {
    end: { x: number; y: number };
    start: { x: number; y: number };
  };
  style?: StyleProp<ViewStyle>;
}

// inspired by https://github.com/apepblack22/expo-animated-linear-gradient/blob/master/index.js
const CLinearGradient = forwardRef<LinearGradient, GradientProps>(
  ({ color0, color1, children, points, style }, ref) => {
    const gStart = points.start;
    const gEnd = points.end;

    return (
      <LinearGradient
        colors={[rgbaToHex(color0), rgbaToHex(color1)]}
        end={gEnd}
        ref={ref}
        start={gStart}
        style={style}
      >
        {children}
      </LinearGradient>
    );
  },
);

const AnimatedLinearGradient =
  Animated.createAnimatedComponent(CLinearGradient);

interface AnimatedGradientProps {
  children?: React.ReactNode;
  colors: [string, string];
  isAnimationTriggered: boolean;
  points?: {
    end: { x: number; y: number };
    start: { x: number; y: number };
  };
  speed?: number;
  style?: StyleProp<ViewStyle>;
}

export const AnimatedGradient: React.FC<AnimatedGradientProps> = ({
  colors,
  points = { end: { x: 0, y: 0 }, start: { x: 1, y: 1 } },
  speed = 350,
  style,
  children,
  isAnimationTriggered,
}) => {
  const [color0] = useState(new Animated.Value(0));
  const [color1] = useState(new Animated.Value(0));
  const theme = useTheme();

  const startAnimation = useCallback(() => {
    [color0, color1].forEach((color) => color.setValue(0));
    Animated.parallel(
      [color0, color1].map((animatedColor) => {
        return Animated.timing(animatedColor, {
          duration: speed,
          easing: Easing.linear,
          toValue: 1,
          useNativeDriver: false,
        });
      }),
    ).start();
  }, [color0, color1, speed]);

  useEffect(() => {
    startAnimation();
  }, [startAnimation, isAnimationTriggered]);

  const interpolatedColors = [color0, color1].map((animatedColor, index) =>
    animatedColor.interpolate({
      inputRange: [0, 1],
      outputRange: [theme.colors.white, colors[index]],
    }),
  );

  return (
    <AnimatedLinearGradient
      color0={interpolatedColors[0]}
      color1={interpolatedColors[1]}
      points={points}
      style={style}
    >
      {children}
    </AnimatedLinearGradient>
  );
};
